export function formatDateTime(isoString: string): {
  date: string;
  time: string;
  timezone: string;
  full: string;
} {
  if (!isoString) {
    return { date: '', time: '', timezone: '', full: '' };
  }

  const date = new Date(isoString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { date: 'Invalid date', time: '', timezone: '', full: 'Invalid date' };
  }
  
  // Use user's local timezone (browser default)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };
  
  const formatter = new Intl.DateTimeFormat('en-AU', options);
  const parts = formatter.formatToParts(date);
  
  // Extract parts
  const day = parts.find(p => p.type === 'day')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const year = parts.find(p => p.type === 'year')?.value || '';
  const hour = parts.find(p => p.type === 'hour')?.value || '';
  const minute = parts.find(p => p.type === 'minute')?.value || '';
  const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || '';
  const timeZoneName = parts.find(p => p.type === 'timeZoneName')?.value || '';
  
  const formattedDate = `${day}/${month}/${year}`;
  const formattedTime = `${hour}:${minute} ${dayPeriod}`;
  const full = `${formattedDate} ${formattedTime} ${timeZoneName}`;
  
  return {
    date: formattedDate,
    time: formattedTime,
    timezone: timeZoneName,
    full
  };
}

/**
 * Format datetime for display with full details
 * Shows in user's local timezone
 * 
 * @param isoString - ISO 8601 datetime string
 * @returns Formatted string like "30/10/2025 11:30 AM AEDT"
 * 
 * @example
 * formatDateTimeDisplay("2025-10-30T14:00:00Z")
 * // Returns: "31/10/2025 01:00 AM AEDT" (if user is in Sydney)
 */
export function formatDateTimeDisplay(isoString: string): string {
  const { full } = formatDateTime(isoString);
  return full;
}

/**
 * Get relative time description (e.g., "2 hours ago", "in 3 days")
 * 
 * @param isoString - ISO 8601 datetime string
 * @returns Human-readable relative time
 * 
 * @example
 * formatRelativeTime("2025-10-30T12:00:00Z") // "2 hours ago"
 * formatRelativeTime("2025-11-01T12:00:00Z") // "in 2 days"
 */
export function formatRelativeTime(isoString: string): string {
  if (!isoString) return 'Unknown time';
  
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return 'Invalid date';
  
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  const isPast = diffMs < 0;
  const suffix = isPast ? 'ago' : 'from now';
  
  if (diffSeconds < 30) {
    return 'Just now';
  } else if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ${suffix}`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ${suffix}`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ${suffix}`;
  } else {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ${suffix}`;
  }
}

/**
 * Convert local Date to ISO string for backend
 * Always sends UTC to backend
 * 
 * @param date - JavaScript Date object
 * @returns ISO 8601 string in UTC (e.g., "2025-10-30T14:00:00.000Z")
 */
export function toISOString(date: Date | undefined): string | undefined {
  if (!date) return undefined;
  return date.toISOString();
}
